import type {
  Key,
  MenuTriggerProps as AriaMenuTriggerProps,
  MenuProps as AriaMenuProps,
  PopoverProps as AriaPopoverProps,
} from "react-aria-components";
import { Menu, MenuItem, MenuTrigger } from "react-aria-components";
import React from "react";
import type { ReactElement } from "react";
import { tv } from "tailwind-variants";

import { cn } from "@/lib/helpers/cn";
import EllipsisVerticalIcon from "@/assets/images/icons2/icon--ellipsis-vertical.svg?component";

import { Button2 } from "../atoms/Button";
import { listBoxItemStyles } from "../atoms/ListBoxItem";
import { Popover2 } from "../atoms/Popover";

type BaseItem<TKey> = {
  id: TKey;
  isDisabled?: boolean;
  label: string;
};

type MenuProps<TKey> = {
  className?: string;
  items: readonly BaseItem<TKey>[];
  onAction?: (key: TKey) => void;
  trigger?: ReactElement;
} & Omit<AriaMenuTriggerProps, "children" | "trigger"> &
  Pick<AriaMenuProps<BaseItem<TKey>>, "selectionMode" | "selectedKeys" | "onSelectionChange"> &
  Pick<AriaPopoverProps, "placement" | "offset" | "crossOffset">;

export function Menu2<TKey extends Key>({
  className,
  crossOffset,
  items,
  offset,
  onAction,
  placement,
  trigger,
  ...triggerProps
}: MenuProps<TKey>) {
  const { menu, popover } = menuStyles();
  const { listBoxItem } = listBoxItemStyles();

  const triggerElement = trigger ?? (
    <Button2 aria-label="Open menu" rawChildren variant="blank">
      <EllipsisVerticalIcon className="tw:text-fill-teal-primary" />
    </Button2>
  );

  return (
    <MenuTrigger {...triggerProps}>
      {triggerElement}

      <Popover2
        className={cn(popover(), className)}
        crossOffset={crossOffset}
        offset={offset}
        placement={placement}
      >
        <Menu className={menu()} items={items} onAction={onAction}>
          {(item) => (
            <MenuItem
              className={cn(listBoxItem(), "atom-keyboard-focus")}
              id={item.id}
              isDisabled={item.isDisabled}
              textValue={item.label}
            >
              {item.label}
            </MenuItem>
          )}
        </Menu>
      </Popover2>
    </MenuTrigger>
  );
}

export const menuStyles = tv({
  slots: {
    menu: cn("tw:max-h-75 tw:overflow-y-auto", "tw:outline-none", "tw:py-1"),
    popover: "tw:min-w-44",
  },
});
